import * as THREE from 'three';

/**
 * Manages full 100% triangle topology wireframe visualization across 3D models.
 * Uses WireframeGeometry to render every single polygon edge without angle suppression,
 * and attaches line segments directly to child meshes for 100% exact alignment and zero Z-fighting.
 */

export class WireframeManager {
  constructor() {
    this.overlayEnabled = false;
    this.pureWireframeEnabled = false;
    this.wireframeColor = '#00f3ff';
  }

  setOverlayMode(enabled) {
    this.overlayEnabled = enabled;
  }

  setPureWireframeMode(enabled) {
    this.pureWireframeEnabled = enabled;
  }

  setWireframeColor(hexColor) {
    this.wireframeColor = hexColor;
  }

  /**
   * Applies full triangle topology wireframes directly to the mesh hierarchy.
   */
  applyWireframeToModel(targetGroup, scene) {
    if (!targetGroup) return;

    targetGroup.traverse((node) => {
      if (node.isMesh && node.geometry) {
        // Remove existing wireframe overlay line child if present
        const existingWire = node.getObjectByName('__wireframe_overlay__');
        if (existingWire) {
          node.remove(existingWire);
          if (existingWire.geometry) existingWire.geometry.dispose();
          if (existingWire.material) existingWire.material.dispose();
        }

        // Apply polygonOffset to solid material to eliminate Z-fighting
        if (node.material) {
          const mats = Array.isArray(node.material) ? node.material : [node.material];
          mats.forEach(m => {
            if (this.overlayEnabled) {
              m.polygonOffset = true;
              m.polygonOffsetFactor = 1;
              m.polygonOffsetUnit = 1;
            } else {
              m.polygonOffset = false;
            }
          });
        }

        // Toggle solid faces visibility if pure wireframe mode
        node.visible = !this.pureWireframeEnabled;

        if (this.overlayEnabled || this.pureWireframeEnabled) {
          // WireframeGeometry renders 100% of all triangle edges without angle suppression
          const wireframeGeo = new THREE.WireframeGeometry(node.geometry);

          const lineMat = new THREE.LineBasicMaterial({
            color: new THREE.Color(this.wireframeColor),
            linewidth: 1,
            transparent: true,
            opacity: this.pureWireframeEnabled ? 0.95 : 0.85,
            depthTest: true
          });

          const lineSegments = new THREE.LineSegments(wireframeGeo, lineMat);
          lineSegments.name = '__wireframe_overlay__';
          lineSegments.position.set(0, 0, 0);
          lineSegments.rotation.set(0, 0, 0);
          lineSegments.scale.set(1, 1, 1);
          lineSegments.matrixAutoUpdate = false;
          lineSegments.visible = true;

          node.add(lineSegments);
        }
      }
    });
  }

  clearWireframe(targetGroup) {
    if (!targetGroup) return;
    targetGroup.traverse((node) => {
      if (node.isMesh) {
        node.visible = true;
        if (node.material) {
          const mats = Array.isArray(node.material) ? node.material : [node.material];
          mats.forEach(m => {
            m.polygonOffset = false;
          });
        }
        const existingWire = node.getObjectByName('__wireframe_overlay__');
        if (existingWire) {
          node.remove(existingWire);
          if (existingWire.geometry) existingWire.geometry.dispose();
          if (existingWire.material) existingWire.material.dispose();
        }
      }
    });
  }
}
